import { DeliveryResult, DeliveryVatTotal } from "./DeliveryOverview";
import { Link } from "react-router-dom";
import type { DeliveryStatus, ScheduledDelivery } from "../../api/types";
import { money } from "../../i18n/intl";
import { deliveryHeadline, deliveryTotals } from "../../utils/deliveryTotals";

const LOOK: Record<DeliveryStatus, { edge: string; pill: string; label: string }> = {
  SCHEDULED: { edge: "dlv-card--scheduled", pill: "dlv-pill dlv-pill--scheduled", label: "Scheduled" },
  IN_PROGRESS: { edge: "dlv-card--progress", pill: "dlv-pill dlv-pill--progress", label: "In progress" },
  COMPLETED: { edge: "dlv-card--complete", pill: "dlv-pill dlv-pill--complete", label: "Completed" },
  CANCELED: { edge: "dlv-card--canceled", pill: "dlv-pill dlv-pill--canceled", label: "Canceled" },
};

const fmtWhen = (iso: string) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/**
 * One delivery, one card, on a phone.
 *
 * The row this replaces was a table 915 px wide inside a 327 px card whose
 * `overflow` was `hidden`, so 488 px of it — the expected total, the status and
 * the Open link — were not merely off-screen but unreachable: you could not
 * scroll to them. The owner's ask was "we should see price of delivery too";
 * the price was there all along, sitting 574 px from the left edge.
 *
 * So the money leads on the right, where the quantity sits on a stock card, and
 * the whole card is the link, which brings back the Open the table lost.
 *
 * G-121 S2: the figure that leads is the supplier's BILLED total when every
 * line of the delivery carries one; otherwise the expected value — labelled
 * "(typed)" only when nobody uploaded an invoice, because an invoice-created
 * delivery whose billed figure is unknown on some line was NOT typed by hand
 * and must not say so. Nobody mistakes a guess for the paper. A shortage
 * reads as "not received": information, not a receivable.
 */
export function DeliveryCard({
  delivery,
  confirmLabel,
}: {
  delivery: ScheduledDelivery;
  /** Florists are confirming stock, not reviewing an order. */
  confirmLabel: boolean;
}) {
  const totals = deliveryTotals(delivery);
  const look = LOOK[delivery.status];
  const lead = deliveryHeadline(totals, delivery.invoice !== null);
  const headline = lead.amount;
  const headlineLabel = lead.label.toLowerCase();
  /*
   * The table told a florist which deliveries were theirs to act on — its last
   * cell read "Confirm →" rather than "Open →". The card swallowed that cue
   * into an aria-label nobody sees (found by /code-review), so it comes back
   * as a visible one, on the same condition the table used: a COMPLETED
   * delivery has nothing left to confirm.
   */
  const toConfirm = confirmLabel && delivery.status !== "COMPLETED";

  return (
    <Link
      to={`/app/deliveries/${delivery.id}`}
      className={`dlv-card ${look.edge}`}
      aria-label={`${delivery.supplier.name} - ${delivery.location.name}, ${money(headline)} ${headlineLabel}, ${
        toConfirm ? "confirm" : "open"
      }`}
    >
      <span className="dlv-card-supplier" style={{ gridColumn: "1 / -1" }}>
        {delivery.supplier.name} - {delivery.location.name}
      </span>

      <div className="dlv-card-money" style={{ gridRow: 2 }}><DeliveryVatTotal delivery={delivery} /></div>

      <span className="dlv-card-when">
        {fmtWhen(delivery.scheduledFor)}
      </span>

      <span className="dlv-card-foot">
        <DeliveryResult delivery={delivery} />
        {/* G-131: a hand-made delivery waits for the factura that bills it. */}
        {!confirmLabel && delivery.facturaWait === "waiting" ? (
          <span className="paper-pill paper-pill--warn">Waiting for a factura</span>
        ) : null}
        {/* G-137 (OD-184): arrived, typed by hand, no factura and no document. */}
        {!confirmLabel && delivery.documentState === "missing" ? (
          <span className="paper-pill paper-pill--warn">No document</span>
        ) : null}
        {toConfirm ? <span className="dlv-card-cue">Confirm →</span> : null}
      </span>

      {totals.shortageEur > 0 ? (
        <span className="dlv-card-shortage">
          not received {money(totals.shortageEur)}
        </span>
      ) : null}
    </Link>
  );
}
